import { useCallback, useMemo, useState } from 'react';
import { type Moment } from 'moment';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import {
  MobileTimePicker,
  MobileTimePickerSlotProps,
} from '@mui/x-date-pickers/MobileTimePicker';

import { TimeView } from '@mui/x-date-pickers';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import {
  TimeFieldProps,
  TimeFieldSlotProps,
} from '@mui/x-date-pickers/TimeField/TimeField.types';
import { colors } from 'src/utils/customColors';

import { timeFormat } from 'src/utils/datetime';
import { Styled } from './index.styled';

interface TimeInputProps {
  name: string;
  value: Moment | null;
  onChange: (newValue: Moment | null) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  allowTyping?: boolean;
  allowPicker?: boolean;
  minutesStep?: number;
  slotProps?: MobileTimePickerSlotProps<Moment, TimeView, boolean>;
  customTimeFieldProps?: TimeFieldProps<Moment, boolean>;
}

const timeFieldSx = { input: { textAlign: 'center' } };

export default function TimeInput({
  name,
  label,
  value,
  onChange,
  helperText,
  slotProps,
  allowTyping,
  allowPicker,
  minutesStep,
  error,
  customTimeFieldProps,
}: TimeInputProps) {
  const [open, setOpen] = useState(false);

  const options = useMemo(
    () => ({
      name,
      value,
      format: timeFormat,
      minutesStep,
      error,
      helperText,
      onChange,
    }),
    [name, value, minutesStep, onChange, error, helperText],
  );

  const preventEventBubbling = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const timeFieldSlotProps = useMemo(() => {
    return {
      textField: {
        fullWidth: true,
        label,
        ...(allowPicker
          ? {
              InputProps: {
                endAdornment: (
                  <AccessTimeOutlinedIcon
                    onClick={handleOpen}
                    sx={{ color: colors.darkerBlue }}
                  />
                ),
              },
            }
          : {}),
      },
    } as TimeFieldSlotProps<Moment, boolean>;
  }, [label, allowPicker]);

  const mobilePickerOptions = useMemo(() => {
    if (!allowTyping) return options;
    return {
      ...options,
      open,
      onClose: handleClose,
      onAccept: handleClose,
    };
  }, [allowTyping, options, open]);

  return (
    <>
      {allowTyping && (
        <TimeField
          variant="outlined"
          slotProps={timeFieldSlotProps}
          {...options}
        />
      )}
      {allowPicker && (
        <Styled.Wrapper
          allowTyping={allowTyping}
          onClick={preventEventBubbling}
        >
          <MobileTimePicker
            ampm={false}
            label={label}
            slotProps={slotProps}
            {...mobilePickerOptions}
          />
        </Styled.Wrapper>
      )}
      {!allowTyping && !allowPicker && (
        <div onClick={preventEventBubbling}>
          <TimeField
            variant="standard"
            sx={timeFieldSx}
            {...options}
            {...customTimeFieldProps}
          />
        </div>
      )}
    </>
  );
}
